/**
 * Demo-only staffing helpers.
 *
 * The patient score: on the hospital demo the Staffing page multiplies the ratio ladder by the
 * shift's patient score, so it shows the same nurse count as the Shift Insights page. Also the
 * shared demo input type (`DemoRequiredInput`) and the hospital day badge numbers.
 */

import type { DateTime } from "luxon";
import { type Day, DepartmentType, isRealDemoDepartmentId, type WeekData } from "./models";

/**
 * What changes a location's required count on a demo department, for one day.
 * - `null`: not a demo, the ladder number is used as is.
 * - `score`: a hospital demo; the ladder number is multiplied by the shift's patient score.
 * - `node`: the node (the shift's staffNo) is split across the rooms, keyed by location id.
 *   A location missing from the map uses the score (or ladder) path. `scoreByShiftType` is that
 *   score, or `null` when the department is not a real hospital demo. `problems` lists what the
 *   split could not place, for the caller to report.
 */
export type DemoRequiredInput =
	| null
	| { kind: "score"; scoreByShiftType: Record<string, number> }
	| {
			kind: "node";
			requiredByLocationId: ReadonlyMap<string, number>;
			scoreByShiftType: Record<string, number> | null;
			problems: readonly NodeSplitProblem[];
	  };

/** Something the node split could not place. */
export type NodeSplitProblem = { message: string; details: Record<string, string | number> };

/** Same gate as `isHospitalView`, for code with the department but no context (the printout). */
export const isHospitalDemoDepartment = (departmentId: string | undefined, departmentType: DepartmentType | undefined): boolean =>
	isRealDemoDepartmentId(departmentId) && departmentType === DepartmentType.Hospital;

/**
 * Nurses needed, times the patient score, rounded to the nearest whole nurse.
 * A `null` score means this is not a demo, so nothing changes.
 *
 * Nearest, not up. This runs once per job, and rounding each one up made the totals too big:
 * a 1-nurse job at score 1.2 became 2. `floor(x + 0.5)` so it matches the Python side.
 */
export const scoreAdjustedNurses = (needed: number, score: number | null): number => (score === null ? needed : Math.floor(needed * score + 0.5));

/**
 * The patient score of each shift on one day, keyed by shift type ("Day" / "Night").
 * Look a location up by its `shift_type`. `null` when this is not a hospital demo.
 */
export const scoreByShiftTypeForDay = (week: WeekData | undefined, dayIndex: number, isRealHospitalDemo: boolean): Record<string, number> | null => {
	if (!isRealHospitalDemo || week === undefined) return null;
	const byShiftType: Record<string, number> = {};
	for (const shift of week.weeklyShifts) {
		// Index access, not `.at()`: an unknown day (-1) must find nothing, not the last day.
		const score = shift.days[dayIndex]?.score;
		if (score !== undefined) byShiftType[shift.type] = score;
	}
	return byShiftType;
};

/**
 * The score for one location, or `null` to change nothing.
 *
 * DEFAULT: a missing shift type returns `null` instead of throwing. Shift Insights has no number
 * there either, so there is nothing to match. Cannot happen in the demo.
 */
export const scoreForShiftType = (scoreByShiftType: Record<string, number> | null, shiftType: string): number | null =>
	scoreByShiftType === null ? null : (scoreByShiftType[shiftType] ?? null);

/** One shift's Day for a given date. */
export type ShiftDayOnDate = { shiftType: string; day: Day };

/**
 * Every shift's Day on `date`, found by date (not by index), across all loaded weeks.
 * Empty when the date is not loaded.
 */
export const shiftDaysOnDate = (weeks: readonly WeekData[], date: DateTime): ShiftDayOnDate[] => {
	const result: ShiftDayOnDate[] = [];
	for (const week of weeks) {
		for (const shift of week.weeklyShifts) {
			const day = shift.days.find((candidate) => candidate.date.hasSame(date, "day"));
			if (day !== undefined) result.push({ shiftType: shift.type, day });
		}
	}
	return result;
};

export type HospitalRatioBadge = { numerator: number; denominator: number; percentage: number };

/**
 * The hospital day badge: staff on the day against the sum of every shift's node (staffNo).
 * 0% when nothing is required, so an empty day never shows a division by zero.
 */
export const hospitalRatioBadge = (totalStaff: number, shiftDays: readonly ShiftDayOnDate[]): HospitalRatioBadge => {
	const denominator = shiftDays.reduce((sum, { day }) => sum + day.staffNo, 0);
	const percentage = denominator > 0 ? Math.round((totalStaff / denominator) * 100) : 0;
	return { numerator: totalStaff, denominator, percentage };
};

/** The hospital day badge for a date. A chip with no (valid) date has no shifts, so it shows 0%. */
export const hospitalRatioBadgeForDate = (totalStaff: number, weeks: readonly WeekData[], date: DateTime | undefined): HospitalRatioBadge =>
	hospitalRatioBadge(totalStaff, date?.isValid ? shiftDaysOnDate(weeks, date) : []);
